import { useState } from 'react'
import { useObjectives, useUpdateObjective } from '@/features/objectives/hooks/useObjectives'
import { ObjectiveCard } from '@/features/objectives/components/ObjectiveCard'
import { CreateObjectiveModal } from '@/features/objectives/components/CreateObjectiveModal'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import type { Objective } from '@/features/objectives/types'
import { useTranslation } from 'react-i18next'

type FilterStatus = 'all' | 'pending' | 'completed'

function ObjectiveSkeleton() {
  return (
    <div className="animate-pulse rounded-xl border border-surface-200 bg-white p-5">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="h-4 w-2/3 rounded bg-surface-100" />
        <div className="h-5 w-16 rounded-full bg-surface-100" />
      </div>
      <div className="h-3 w-full rounded bg-surface-100" />
      <div className="mt-1 h-3 w-4/5 rounded bg-surface-100" />
    </div>
  )
}

export function ObjectivesPage() {
  const { t } = useTranslation()
  const [modalOpen, setModalOpen] = useState(false)
  const [filter, setFilter] = useState<FilterStatus>('all')

  const { data: objectives = [], isLoading, isError } = useObjectives()
  const updateMutation = useUpdateObjective()

  function handleUpdate(id: string, status: string) {
    updateMutation.mutate({
      id,
      data: { status: status as Objective['status'] },
    })
  }

  const filtered =
    filter === 'all'
      ? objectives
      : objectives.filter((o) => o.status === filter)

  const tabs: { key: FilterStatus; labelKey: string }[] = [
    { key: 'all', labelKey: 'objectives.tabs.all' },
    { key: 'pending', labelKey: 'objectives.tabs.pending' },
    { key: 'completed', labelKey: 'objectives.tabs.completed' },
  ]

  return (
    <main className="min-h-screen bg-surface-50">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold text-neutral-900">{t('objectives.page_title')}</h1>
          <Button onClick={() => setModalOpen(true)}>{t('objectives.new')}</Button>
        </div>

        {isError && (
          <Alert variant="error" className="mb-6">
            {t('objectives.load_error')}
          </Alert>
        )}

        <div className="mb-6 flex gap-1 rounded-lg bg-surface-100 p-1 w-fit">
          {tabs.map(({ key, labelKey }) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={[
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                filter === key
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900',
              ].join(' ')}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <ObjectiveSkeleton key={i} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-surface-200 bg-white p-10 text-center">
            <p className="text-sm font-medium text-neutral-900">
              {filter === 'all'
                ? t('objectives.empty_all')
                : t('objectives.empty_filtered')}
            </p>
            {filter === 'all' && (
              <p className="mb-3 mt-1 text-sm text-neutral-600">{t('objectives.empty_hint')}</p>
            )}
            {filter === 'all' && (
              <Button
                onClick={() => setModalOpen(true)}
                variant="secondary"
                size="sm"
              >
                {t('objectives.create_first')}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((objective) => (
              <ObjectiveCard
                key={objective.id}
                objective={objective}
                onUpdate={handleUpdate}
              />
            ))}
          </div>
        )}

        <CreateObjectiveModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      </div>
    </main>
  )
}
